import {Component, ComponentState, ComponentSummary} from '../@types/component';
import {TreeViewItem} from '../@types/tree-view-item';
import {ResourceType} from '../@types/resource-type';
import Group from '../@types/group';
import {makeGroup, role} from './group-utils';
import {getResourceNameWithAscendants} from './resource-utils';

function toState(raw: unknown): ComponentState {
    return Object.values(ComponentState).includes(raw as ComponentState)
        ? (raw as ComponentState)
        : ComponentState.DRAFT;
}

function toDate(raw: unknown): Date | undefined {
    return raw ? new Date(raw as string) : undefined;
}

/**
 * Maps a raw backend ComponentSummaryDTO -- or an already-mapped {@link ComponentSummary} -- to a
 * {@link ComponentSummary}. Idempotent so it is safe as a tab-editor {@code prepareObject}.
 */
export function makeComponentSummary(raw: any): ComponentSummary {
    return {
        id: raw.id,
        uniqueId: raw.uniqueId ?? '',
        name: raw.name ?? '',
        description: raw.description ?? '',
        releaseNotes: raw.releaseNotes || undefined,
        version: raw.version || undefined,
        state: toState(raw.state),
        groupId: raw.groupId ?? raw.group?.id,
        group: raw.group,
        created: toDate(raw.created),
        updated: toDate(raw.updated),
        sourcesRunAgainst: raw.sourcesRunAgainst ?? undefined,
        lastRunAt: raw.lastRunAt ?? undefined,
        mappedSources: raw.mappedSources ?? undefined,
    };
}

/**
 * Maps a raw backend ComponentDTO to a {@link Component} (summary fields plus the fault tree,
 * when the endpoint returned one).
 */
export function makeComponent(raw: any): Component {
    return {
        ...makeComponentSummary(raw),
        faultTreeId: raw.faultTreeId,
        faultTree: raw.faultTree,
    };
}

/**
 * Maps Component list rows to explorer tree items. Each raw item is a ComponentSummary; {@code
 * raw.group}, when present, is the plain GroupDTO shape straight off the wire (not a {@link Group}
 * instance -- role() and friends are methods on the class, not the JSON), so it is run through
 * {@link makeGroup} first.
 */
export function componentTreeItems(components: any[]): TreeViewItem[] {
    return components.map((raw) => {
        const summary: ComponentSummary = makeComponentSummary(raw);
        const group: Group | undefined = raw.group ? makeGroup(raw.group) : undefined;
        return {
            id: summary.id.toString(),
            object: summary,
            name: summary.version ? `${summary.name} (v${summary.version})` : `${summary.name} (draft)`,
            description: summary.description,
            status: summary.state,
            version: summary.version,
            type: ResourceType.COMPONENT,
            hasChildren: false,
            sortField: summary.name,
            groupPath: group ? getResourceNameWithAscendants(group, ResourceType.GROUPS) : '',
            groupRole: group ? role(group) : '',
        };
    });
}

/**
 * Whether a workspace object is a Component's fault tree, whichever editor is showing it.
 *
 * <p>The node modals branch on this to offer the full set of node classes, because a Component's
 * fault tree is not an event sequence diagram and initiating/pivotal placement does not apply to
 * it. Named rather than compared inline because getting it wrong is silent: a Component's nodes
 * simply lose PRECURSOR and INDUCED from the dropdown.</p>
 *
 * @param objectType the open workspace object's type
 */
export function isComponentFaultTreeContainer(objectType?: ResourceType | null): boolean {
    // COMPONENT_TREE is the canvas; COMPONENT is the details form, which has no fault tree on it.
    return objectType === ResourceType.COMPONENT_TREE;
}

/**
 * Whether the current user may change a Component: only a DRAFT, and only with add/edit rights in
 * its owning working area. Published releases are read-only for everyone.
 *
 * @param component the Component, or undefined when none is open
 * @param groups    the working areas the user can see, with their roles
 */
export function canEditComponentDraft(
    component: {groupId?: number; state?: ComponentState} | undefined,
    groups: Group[],
): boolean {
    if (!component || component.state !== ComponentState.DRAFT || !component.groupId) {
        return false;
    }
    const ownerGroup = groups.find((grp) => grp.id === component.groupId);
    return ownerGroup ? ownerGroup.canAddOrEditResources() : false;
}
